import React, { FC, ReactNode, useEffect } from "react";
import classNames from "classnames";
import { useSelect, UseSelectProps } from "downshift";

import { FieldStatus } from "../../../Helpers/FieldStatus";
import { interpolate as inter } from "../../../Helpers";
import { DoctorDto } from "../../../../../../Api/Model/DoctorDto.csharp";
import { ServiceContainerDto } from "../../../../../../Api/Model/ServiceContainerDto.csharp";
import { LocationDto } from "../../../../../../Api/Model/LocationDto.csharp";
import { LoadingAnimation } from "../../../../LoadingAnimation/LoadingAnimation";
import { CommonBookingLabelsDto } from "../../../../../../Api/Model/CommonBookingLabelsDto.csharp";
import { intersect } from "../../../../Helpers/setsOperationsHelpers";
import { doctorInitialState } from "../../../Helpers/initialState/doctorInitialState";
import { SvgIcon } from "../../../../SvgIcon/SvgIcon";

type LocationDropdownProps = {
  locations: UseSelectProps<LocationDto | null>;
  label: string;
  placeholder: string;
  onSelect: (location: LocationDto) => void;
  subHeadingFunction?: (forField: string) => string;
  unavailableLocations: {
    items: LocationDto[];
  };
  status?: FieldStatus;
  error?: {
    message?: string;
  };
  dependencies?: string[];
  defaultValue?: LocationDto;
  doctor: DoctorDto;
  onDoctorSelect: (doctor: DoctorDto) => void;
  service: ServiceContainerDto;
  additionalLabels: CommonBookingLabelsDto;
};

export const LocationDropdownLegacy: FC<LocationDropdownProps> = ({
  locations,
  label,
  placeholder,
  onSelect,
  unavailableLocations = {
    items: [],
  },
  status = FieldStatus.active,
  error,
  dependencies = [],
  defaultValue = undefined,
  doctor,
  onDoctorSelect,
  service,
  additionalLabels,
}) => {
  const {
    isOpen,
    selectedItem,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    highlightedIndex,
    getItemProps,
    selectItem,
  } = useSelect(locations);

  useEffect(() => {
    selectItem(null);
  }, [...dependencies]);

  useEffect(() => {
    const shouldResetDoctor =
      !!selectedItem &&
      ((!!selectedItem.id &&
        selectedItem.isLocation &&
        intersect(doctor.centers, selectedItem.centers).length == 0) ||
        (!selectedItem.isLocation &&
          !doctor.centers.includes(selectedItem.id)));
    if (shouldResetDoctor) {
      onDoctorSelect(doctorInitialState);
    }

    if (selectedItem != null && selectedItem.id) {
      onSelect(selectedItem);
    }
  }, [selectedItem]);

  function getUnavailableLocationsSubheading() {
    if (
      doctor.id &&
      doctor.id != "-1" &&
      doctor.services.indexOf(service.id) == -1
    )
      return inter(
        additionalLabels.locationNotByDoctor,
        "$doctor",
        doctor.firstName
      );
    if (service.id)
      return inter(
        additionalLabels.locationNotByService,
        "$service",
        service.bookingName
      );
    return "";
  }

  const getLabelForOnlyOneCenter = (item: LocationDto | null) => {
    if (item?.concatCenterLocationInLocationDropdown) {
      const location = locations.items.find((l) => l?.id == item.locationId);

      return `${item.name} (${location?.name})`;
    }

    return item?.name;
  };

  const mapAvailableLocations = (
    item: LocationDto | null,
    index: number
  ): ReactNode => {
    let label = item?.name;
    const isLocation = item?.isLocation && item?.id != "-1";

    if (isLocation) {
      const centers = locations.items.filter((x) => x?.locationId == item.id);

      if (centers.length < 2) {
        return;
      }

      label = `${item!.name} (alle)`;
    }

    const onlyOneCenterInLocation =
      item?.isLocation == false &&
      locations.items.filter((l) => l?.locationId == item.locationId).length ==
        1;

    if (onlyOneCenterInLocation) {
      label = getLabelForOnlyOneCenter(item);
    }

    return (
      <>
        {(isLocation || onlyOneCenterInLocation) && <span className='Dropdown__optionsList--separator'/>}
        <li
          key={item!.id}
          className={classNames("Dropdown__option", {
            "Dropdown__option--groupHeader":
            item?.isLocation || onlyOneCenterInLocation,
            "Dropdown__option--highlighted": highlightedIndex === index,
          })}
          {...getItemProps({ item, index })}
          >
          {label}
        </li>
        {(locations.items.length - 1 === index) && <span className='Dropdown__optionsList--separator'/>}
      </>
    );
  };

  const mapUnavailableLocations = (item: LocationDto | null) => {
    return (
      <li key={item!.id} className="Dropdown__option Dropdown__option--disabled">
        {item!.name}
      </li>
    );
  };

  const madeSelection =
    selectedItem != null || (defaultValue && defaultValue.id);

  const selectedValueLabel = selectedItem?.name ?? defaultValue?.name ?? "";

  const renderDropdownLabel = () => {
    if (status === FieldStatus.disabled && madeSelection) {
      return <LoadingAnimation text={selectedValueLabel} />;
    }
    if (!madeSelection && status === FieldStatus.disabled) {
      return <LoadingAnimation text={placeholder} />;
    }
    if (!madeSelection && status === FieldStatus.active) {
      return placeholder;
    }
    return selectedValueLabel;
  };

  return (
    <div
      className={classNames(
        "Dropdown Dropdown--grouped",
        {
          "Dropdown--opened": isOpen,
          "Dropdown--disabled": status == FieldStatus.disabled,
          "Dropdown--error": status == FieldStatus.error,
        }
      )}
    >
      <span {...getLabelProps()} className="Dropdown__label">
        {label}
      </span>
      <button
        className="Dropdown__field"
        type="button"
        {...getToggleButtonProps()}
        disabled={status == FieldStatus.disabled}
      >
        {renderDropdownLabel()}
        {status != FieldStatus.disabled && <SvgIcon iconName="chevron-down" className="Dropdown__icon" />}
      </button>
      <ul className="Dropdown__optionsList" {...getMenuProps()}>
        {isOpen && (
          <>
            {locations.items.map(mapAvailableLocations)}
            {unavailableLocations.items.length > 0 && (
              <>
                <li className="Dropdown__option Dropdown__option--disabled Dropdown__option--groupHeader">
                  {getUnavailableLocationsSubheading()}
                </li>
                {unavailableLocations.items.map(mapUnavailableLocations)}
              </>
            )}
          </>
        )}
      </ul>
      {status == FieldStatus.error && error && error.message && (
        <span>{error.message}</span>
      )}
    </div>
  );
};
